import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";

import {
  Card,
  EmptyState,
  Pill,
  SearchInput,
  SelectField,
  SkeletonRows,
} from "@/components/ui";
import { formatRelativeTime } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import {
  auditRepository,
  type AuditEntry,
} from "@/repositories/auditRepository";

/**
 * Who did what, and why.
 *
 * The screen an owner opens when a figure looks wrong, so it is built around the
 * question they actually arrive with — "what happened to receipt T01-000124?" or
 * "who has been giving discounts?" — rather than around a table of columns. One
 * search box covers receipt numbers, people and reasons, because somebody
 * looking for "Nomsa" should not first have to decide which field that is.
 *
 * The **reason** is given the same weight as the action. An entry that says a
 * sale was voided is bookkeeping; one that says it was voided because the
 * customer changed their mind at the counter is an answer.
 */
export function AuditLogPage() {
  const [term, setTerm] = useState("");
  const [action, setAction] = useState("");
  const debounced = useDebounced(term, 250);

  const actions = useQuery({
    queryKey: ["audit", "actions"],
    queryFn: auditRepository.actions,
    staleTime: Infinity,
  });

  const entries = useQuery({
    queryKey: ["audit", "entries", debounced, action],
    queryFn: () =>
      auditRepository.entries({ q: debounced, action: action || undefined }),
    placeholderData: (previous) => previous,
  });

  const rows = normalise(entries.data);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Receipt number, person or reason"
          containerClassName="w-full flex-1 sm:w-auto sm:min-w-[16rem]"
        />
        <SelectField
          label=""
          value={action}
          onChange={(event) => setAction(event.target.value)}
          className="w-56"
          options={[
            { value: "", label: "Everything" },
            ...(actions.data ?? []).map((row) => ({
              value: row.value,
              label: row.label,
            })),
          ]}
        />
      </div>

      <Card>
        {entries.isLoading ? (
          <SkeletonRows rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing recorded yet"
            description="Voids, refunds, cash-ups, discounts above the limit and stock adjustments appear here."
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((entry) => (
              <EntryRow key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function EntryRow({ entry }: { entry: AuditEntry }) {
  const change = describeChange(entry);

  return (
    <li className="flex flex-wrap items-start gap-3 py-3">
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-900">
            {entry.action_display}
          </span>
          {entry.entity_label && (
            <span className="text-sm tabular-nums text-ink-600">
              {entry.entity_label}
            </span>
          )}
          {change && <Pill tone="info">{change}</Pill>}
        </span>

        {entry.reason && (
          <p className="mt-0.5 text-sm text-ink-600">“{entry.reason}”</p>
        )}

        <p className="mt-0.5 text-xs text-ink-500">
          {entry.actor_name || "System"}
          {entry.actor_role && ` · ${entry.actor_role}`}
          {" · "}
          {formatRelativeTime(entry.created_at)}
          {entry.origin === "TILL" && " · at a till"}
        </p>
      </span>

      {entry.approved_by_name && (
        <span className="flex items-center gap-1.5 text-xs text-ink-600">
          <ShieldCheck size={14} className="text-brand-600" aria-hidden />
          Approved by {entry.approved_by_name}
        </span>
      )}
    </li>
  );
}

/**
 * "R89.00 → R55.00", where there is a before and an after worth showing.
 *
 * Rendered from the stored data rather than stored as a sentence, because the
 * sentence a reader wants depends on who is reading — and because a stored
 * sentence cannot be re-read when the format changes.
 */
function describeChange(entry: AuditEntry): string | null {
  const before = entry.before as Record<string, string | null>;
  const after = entry.after as Record<string, string | null>;

  for (const key of ["amount", "on_hand", "variance", "refunded"]) {
    if (after[key] == null) continue;
    return before[key] != null
      ? `${before[key]} → ${after[key]}`
      : String(after[key]);
  }
  return null;
}

function normalise(data: unknown): AuditEntry[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as AuditEntry[];
  return (data as { results: AuditEntry[] }).results ?? [];
}
